import { ExerciseType, ExerciseState, RepContext, PoseLandmark } from '../types';
import { calculateAngle, calculateDistance } from './geometry';
import { EXERCISE_CONFIG } from '../constants';

const VISIBILITY_THRESHOLD = 0.5;
const REP_DEBOUNCE_MS = 500; // 0.5 second refractory period between reps

export class ExerciseEngine {
  private context: RepContext;
  private exerciseType: ExerciseType;
  private lastSpokenFeedback: number = 0;
  private lastFeedbackText: string = "";

  // --- NEW: Landmark Smoothing Properties ---
  private smoothedLandmarks: PoseLandmark[] | null = null;
  // This value balances smoothness and responsiveness. Lower = smoother, Higher = more responsive.
  private smoothingFactor: number = 0.3;
  
  // Track min/max values during a rep to assess quality
  private repMinMetric: number = 180; 

  constructor(type: ExerciseType) {
    this.exerciseType = type;
    this.context = {
      count: 0,
      state: ExerciseState.IDLE, // Start in IDLE state
      feedback: "Get into position",
      lastRepTime: 0,
      formScore: 100,
    };
  }

  public getContext() {
    return this.context;
  }

  public analyze(landmarks: PoseLandmark[]): RepContext {
    if (!landmarks || landmarks.length < 33) return this.context;

    // --- NEW: Landmark Smoothing via Exponential Moving Average (EMA) ---
    // This reduces jitter from the camera/model and stabilizes rep counting.
    if (!this.smoothedLandmarks) {
      // On the first frame, initialize with a deep copy of the raw landmarks.
      this.smoothedLandmarks = landmarks.map(lm => ({...lm}));
    } else {
      // Apply EMA to smooth each landmark's position and visibility.
      for (let i = 0; i < landmarks.length; i++) {
        const newLm = landmarks[i];
        const oldLm = this.smoothedLandmarks[i];

        if (newLm && oldLm) {
          oldLm.x = this.smoothingFactor * newLm.x + (1 - this.smoothingFactor) * oldLm.x;
          oldLm.y = this.smoothingFactor * newLm.y + (1 - this.smoothingFactor) * oldLm.y;
          oldLm.z = this.smoothingFactor * newLm.z + (1 - this.smoothingFactor) * oldLm.z;
          oldLm.visibility = this.smoothingFactor * (newLm.visibility || 0) + (1 - this.smoothingFactor) * (oldLm.visibility || 0);
        }
      }
    }
    // All subsequent logic will now use the stabilized `smoothedLandmarks`.
    const currentLandmarks = this.smoothedLandmarks;
    // --- END: Landmark Smoothing ---

    switch (this.exerciseType) {
      case ExerciseType.SQUAT:
        this.analyzeSquat(currentLandmarks);
        break;
      case ExerciseType.PUSHUP:
        this.analyzePushup(currentLandmarks);
        break;
      case ExerciseType.JUMPING_JACK:
        this.analyzeJumpingJack(currentLandmarks);
        break;
      case ExerciseType.SITUP:
        this.analyzeSitup(currentLandmarks);
        break;
    }

    return { ...this.context };
  }

  private speak(text: string) {
    const now = Date.now();
    
    // Update UI feedback immediately
    this.context.feedback = text;

    // Debounce audio speech significantly to prevent audio lag
    if (text !== this.lastFeedbackText || now - this.lastSpokenFeedback > 2500) {
      this.lastSpokenFeedback = now;
      this.lastFeedbackText = text;
      
      if ('speechSynthesis' in window) {
        // Cancel previous speech to ensure snappy response
        window.speechSynthesis.cancel(); 
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.2; // Slightly faster speech
        window.speechSynthesis.speak(utterance);
      }
    }
  }

  private checkVisibility(landmarks: PoseLandmark[]): boolean {
    return landmarks.every(l => l && (l.visibility || 0) > VISIBILITY_THRESHOLD);
  }

  private getDominantSide(landmarks: PoseLandmark[]): number[] {
    const leftIndices = [11, 13, 15, 23, 25, 27];
    const rightIndices = [12, 14, 16, 24, 26, 28];
    
    const leftScore = leftIndices.reduce((acc, idx) => acc + (landmarks[idx]?.visibility || 0), 0);
    const rightScore = rightIndices.reduce((acc, idx) => acc + (landmarks[idx]?.visibility || 0), 0);

    return leftScore >= rightScore ? leftIndices : rightIndices;
  }

  private updateFormScore(deduction: number) {
    // Moving average for form score
    const newScore = Math.max(0, 100 - deduction);
    this.context.formScore = Math.round((this.context.formScore * 0.8) + (newScore * 0.2));
  }

  // --- EXERCISE LOGIC ---

  private analyzeSquat(landmarks: PoseLandmark[]) {
    const config = EXERCISE_CONFIG[ExerciseType.SQUAT];
    const [sIdx, , , hIdx, kIdx, aIdx] = this.getDominantSide(landmarks);
    const hip = landmarks[hIdx];
    const knee = landmarks[kIdx];
    const ankle = landmarks[aIdx];
    const shoulder = landmarks[sIdx];

    if (!this.checkVisibility([hip, knee, ankle, shoulder])) {
      this.context.feedback = "Full body required";
      return;
    }

    const kneeAngle = calculateAngle(hip, knee, ankle);
    
    // STATE MACHINE
    if (this.context.state === ExerciseState.IDLE) {
      this.context.feedback = "Stand straight to begin";
      if (kneeAngle > 170) {
        this.context.state = ExerciseState.UP;
        this.speak("Ready");
      }
      return;
    }
    
    if (this.context.state === ExerciseState.UP) {
      this.repMinMetric = 180; 

      if (kneeAngle < config.thresholdDown) {
        this.context.state = ExerciseState.DOWN;
      } else if (kneeAngle < 130) {
        this.context.feedback = "Lower";
      } else {
        this.context.feedback = "Squat";
      }
    } else if (this.context.state === ExerciseState.DOWN) {
      this.repMinMetric = Math.min(this.repMinMetric, kneeAngle);

      if (kneeAngle > config.thresholdUp) {
        const now = Date.now();
        if (now - this.context.lastRepTime > REP_DEBOUNCE_MS) {
            this.context.state = ExerciseState.UP;
            this.context.count += 1;
            this.context.lastRepTime = now;
            
            let deduction = 0;
            if (this.repMinMetric > config.thresholdDown + 10) deduction = 50;
            else if (this.repMinMetric > config.thresholdDown) deduction = 20;
            
            this.updateFormScore(deduction);
            this.speak(deduction > 0 ? "Deeper" : "Good");
        }
      }
    }
  }

  private analyzePushup(landmarks: PoseLandmark[]) {
    const config = EXERCISE_CONFIG[ExerciseType.PUSHUP];
    
    // IDLE state check before anything else
    if (this.context.state === ExerciseState.IDLE) {
        this.context.feedback = "Hold plank to begin";
        const [sIdx, eIdx, wIdx, hIdx, , aIdx] = this.getDominantSide(landmarks);
        if (this.checkVisibility([landmarks[sIdx], landmarks[eIdx], landmarks[wIdx], landmarks[hIdx], landmarks[aIdx]])) {
            const bodyAlignment = calculateAngle(landmarks[sIdx], landmarks[hIdx], landmarks[aIdx]);
            const elbowAngle = calculateAngle(landmarks[sIdx], landmarks[eIdx], landmarks[wIdx]);
            if (bodyAlignment > 150 && elbowAngle > 150) {
                this.context.state = ExerciseState.UP;
                this.speak("Ready");
            }
        }
        return;
    }
    
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftElbow = landmarks[13];
    const rightElbow = landmarks[14];
    const leftWrist = landmarks[15];
    const rightWrist = landmarks[16];

    const shoulderWidth = calculateDistance(leftShoulder, rightShoulder);
    const isFrontView = shoulderWidth > 0.15;

    if (isFrontView) {
        if (!this.checkVisibility([leftShoulder, rightShoulder, leftElbow, rightElbow])) {
            this.context.feedback = "Camera: Upper Body";
            return;
        }

        const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
        const avgElbowY = (leftElbow.y + rightElbow.y) / 2;
        
        const heightDiff = avgElbowY - avgShoulderY;
        const normalizedDepth = heightDiff / shoulderWidth;
        
        const leftHip = landmarks[23];
        const rightHip = landmarks[24];
        if (this.checkVisibility([leftHip, rightHip])) {
            const avgHipY = (leftHip.y + rightHip.y) / 2;
            if (Math.abs(avgHipY - avgShoulderY) > shoulderWidth * 0.25) {
                this.context.feedback = "Straighten back";
                return; 
            }
        }

        if (this.context.state === ExerciseState.UP) {
            this.repMinMetric = 100;
            if (normalizedDepth < 0.35) {
                this.context.state = ExerciseState.DOWN;
            } else if (normalizedDepth < 0.6) {
                this.context.feedback = "Lower";
            } else {
                this.context.feedback = "Pushup";
            }
        } else if (this.context.state === ExerciseState.DOWN) {
            this.repMinMetric = Math.min(this.repMinMetric, normalizedDepth);

            if (normalizedDepth > 0.75) {
                const now = Date.now();
                if (now - this.context.lastRepTime > REP_DEBOUNCE_MS) {
                    this.context.state = ExerciseState.UP;
                    this.context.count += 1;
                    this.context.lastRepTime = now;
                    
                    let deduction = 0;
                    if (this.repMinMetric > 0.3) deduction = 30;
                    
                    this.updateFormScore(deduction);
                    this.speak(deduction > 0 ? "Chest to floor" : "Good");
                }
            }
        }

    } else {
        const [sIdx, eIdx, wIdx, hIdx, , aIdx] = this.getDominantSide(landmarks);
        
        if (!this.checkVisibility([landmarks[sIdx], landmarks[eIdx], landmarks[wIdx]])) {
            this.context.feedback = "Camera: Side View";
            return;
        }

        const bodyAlignment = calculateAngle(landmarks[sIdx], landmarks[hIdx], landmarks[aIdx]);
        if (this.context.state === ExerciseState.UP && bodyAlignment < 140) {
             this.context.feedback = "Straighten back";
        }

        const elbowAngle = calculateAngle(landmarks[sIdx], landmarks[eIdx], landmarks[wIdx]);

        if (this.context.state === ExerciseState.UP) {
            this.repMinMetric = 180;
            if (elbowAngle < config.thresholdDown) {
                this.context.state = ExerciseState.DOWN;
            } else if (elbowAngle < 120) {
                this.context.feedback = "Chest to floor";
            } else {
                this.context.feedback = "Pushup";
            }
        } else if (this.context.state === ExerciseState.DOWN) {
            this.repMinMetric = Math.min(this.repMinMetric, elbowAngle);
            
            if (elbowAngle > config.thresholdUp) {
                const now = Date.now();
                if (now - this.context.lastRepTime > REP_DEBOUNCE_MS) {
                    this.context.state = ExerciseState.UP;
                    this.context.count += 1;
                    this.context.lastRepTime = now;
                    
                    let deduction = 0;
                    if (this.repMinMetric > config.thresholdDown + 15) deduction = 50;
                    else if (this.repMinMetric > config.thresholdDown) deduction = 20;
                    
                    this.updateFormScore(deduction);
                    this.speak(deduction > 0 ? "Lower" : "Good");
                }
            }
        }
    }
  }

  private analyzeJumpingJack(landmarks: PoseLandmark[]) {
    const config = EXERCISE_CONFIG[ExerciseType.JUMPING_JACK];
    
    const leftShoulder = landmarks[11];
    const leftHip = landmarks[23];
    const leftElbow = landmarks[13];
    const rightShoulder = landmarks[12];
    const rightHip = landmarks[24];
    const rightElbow = landmarks[14];

    if (!this.checkVisibility([leftShoulder, rightShoulder])) {
        this.context.feedback = "Full body";
        return;
    }
    
    const leftArmAngle = calculateAngle(leftHip, leftShoulder, leftElbow);
    const rightArmAngle = calculateAngle(rightHip, rightShoulder, rightElbow);
    const avgArmAngle = (leftArmAngle + rightArmAngle) / 2;

    if (this.context.state === ExerciseState.IDLE) {
        this.context.feedback = "Stand with arms at sides";
        if (avgArmAngle < 40) {
            this.context.state = ExerciseState.DOWN;
            this.speak("Ready");
        }
        return;
    }

    if (this.context.state === ExerciseState.DOWN) {
        if (avgArmAngle > config.thresholdDown) {
            this.context.state = ExerciseState.UP;
        }
    } else if (this.context.state === ExerciseState.UP) {
        if (avgArmAngle < config.thresholdUp + 25) {
            const now = Date.now();
            if (now - this.context.lastRepTime > REP_DEBOUNCE_MS) {
                this.context.state = ExerciseState.DOWN;
                this.context.count += 1;
                this.context.lastRepTime = now;
                this.speak("Good");
            }
        }
    }
  }

  private analyzeSitup(landmarks: PoseLandmark[]) {
    const config = EXERCISE_CONFIG[ExerciseType.SITUP];
    const [sIdx, , , hIdx, kIdx] = this.getDominantSide(landmarks);

    if (!this.checkVisibility([landmarks[sIdx], landmarks[hIdx], landmarks[kIdx]])) {
        this.context.feedback = "Side view required";
        return;
    }
    
    const hipAngle = calculateAngle(landmarks[sIdx], landmarks[hIdx], landmarks[kIdx]);
    
    if (this.context.state === ExerciseState.IDLE) {
        this.context.feedback = "Lie on your back to begin";
        if (hipAngle > 160) {
            this.context.state = ExerciseState.UP;
            this.speak("Ready");
        }
        return;
    }
    
    if (this.context.state === ExerciseState.UP) { 
        this.repMinMetric = 180; 
        if (hipAngle < config.thresholdDown) {
            this.context.state = ExerciseState.DOWN;
        } else {
             this.context.feedback = "Sit up fully";
        }
    } else if (this.context.state === ExerciseState.DOWN) {
        this.repMinMetric = Math.min(this.repMinMetric, hipAngle);

        if (hipAngle > config.thresholdUp) {
            const now = Date.now();
            if (now - this.context.lastRepTime > REP_DEBOUNCE_MS) {
                this.context.state = ExerciseState.UP;
                this.context.count += 1;
                this.context.lastRepTime = now;

                let deduction = 0;
                if (this.repMinMetric > config.thresholdDown + 15) deduction = 40;
                
                this.updateFormScore(deduction);
                this.speak(deduction > 0 ? "Higher" : "Good");
            }
        }
    }
  }
}
